/**
 * Human-readable diff between the saved pair settings signature and the one
 * currently on the form. Both inputs are the JSON strings produced by
 * `settingsSignature` in PairTrading/index.tsx, so the shape is shared by
 * construction: scalar fields plus a sorted `stocks` list of
 * `exchange:SYMBOL@ratio#refPrice#flashLimit` strings.
 */
export interface PairSettingsChange {
    label: string;
    from: string;
    to: string;
}

const FIELD_LABELS: Record<string, string> = {
    closeType: 'Close type',
    takeProfitReturn: 'Take profit (%)',
    cutLossReturn: 'Cut loss (%)',
    maxOpen: 'Max open',
    activeSide: 'Active side',
    futureInstrumentPrice: 'Future reference price',
    isPause: 'Pause opening',
    openMode: 'Mode',
    maxHoldingDays: 'Max holding days',
    stockProportion: 'Stock weight',
    derivPortfolioId: 'Derivative portfolio',
    balanceMode: 'Balance mode',
};

// Fields stored as an enum the user never sees: show the name the form uses.
const VALUE_LABELS: Record<string, Record<string, string>> = {
    openMode: { IMMEDIATE: 'Auto', REFERENCE: 'Waiting' },
};

const fmt = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};

interface StockEntry { symbol: string; ratio: string; refPrice: string; flashLimit: string }

// `exchange:SYMBOL@ratio#refPrice#flashLimit`; exchange may be empty.
const parseStock = (s: string): StockEntry => {
    const [head, ...rest] = s.split('#');
    const [refPrice = '', flashLimit = ''] = rest;
    const at = head.lastIndexOf('@');
    const ratio = at >= 0 ? head.slice(at + 1) : '';
    const key = at >= 0 ? head.slice(0, at) : head;
    const symbol = key.includes(':') ? key.slice(key.indexOf(':') + 1) : key;
    return { symbol, ratio, refPrice, flashLimit };
};

const describeStock = (e: StockEntry): string =>
    `ratio ${fmt(e.ratio)} · ref ${fmt(e.refPrice)} · flash ${fmt(e.flashLimit)}`;

const safeParse = (json: string | null): Record<string, unknown> | null => {
    if (!json) return null;
    try {
        const parsed = JSON.parse(json);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

/**
 * Returns one entry per changed field. An unparseable/missing `saved` yields
 * a single "no saved baseline" entry so the caller still has something to show.
 */
export function diffPairSettings(saved: string | null, current: string): PairSettingsChange[] {
    const a = safeParse(saved);
    const b = safeParse(current);
    if (!a || !b) {
        return [{ label: 'Configuration', from: 'not saved yet', to: 'current form' }];
    }

    const changes: PairSettingsChange[] = [];
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    keys.delete('stocks');
    Array.from(keys).sort().forEach((k) => {
        const av = a[k];
        const bv = b[k];
        if (JSON.stringify(av ?? null) === JSON.stringify(bv ?? null)) return;
        const show = (v: unknown) => fmt(VALUE_LABELS[k]?.[String(v)] ?? v);
        changes.push({ label: FIELD_LABELS[k] ?? k, from: show(av), to: show(bv) });
    });

    const savedStocks = new Map<string, StockEntry>();
    const currStocks = new Map<string, StockEntry>();
    (Array.isArray(a.stocks) ? a.stocks : []).forEach((s) => { const e = parseStock(String(s)); savedStocks.set(e.symbol, e); });
    (Array.isArray(b.stocks) ? b.stocks : []).forEach((s) => { const e = parseStock(String(s)); currStocks.set(e.symbol, e); });

    const symbols = Array.from(new Set([...savedStocks.keys(), ...currStocks.keys()])).sort();
    symbols.forEach((sym) => {
        const before = savedStocks.get(sym);
        const after = currStocks.get(sym);
        if (before && !after) {
            changes.push({ label: `Stock ${sym}`, from: describeStock(before), to: 'removed' });
        } else if (!before && after) {
            changes.push({ label: `Stock ${sym}`, from: 'not in basket', to: describeStock(after) });
        } else if (before && after && describeStock(before) !== describeStock(after)) {
            changes.push({ label: `Stock ${sym}`, from: describeStock(before), to: describeStock(after) });
        }
    });

    return changes;
}
